"use client";

import { ArrowRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Modal, SearchInput } from "@/components/ui-system";
import { filterSidebarGroupsForUser, flattenNavForPalette } from "@/lib/navigation/sidebar-nav";
import { cn } from "@/lib/utils/cn";
import { useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";
import { useCommandPaletteStore } from "@/store/command-palette";

export function CommandPalette() {
  const routerNavigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const permissions = useEffectivePermissions();
  const open = useCommandPaletteStore((state) => state.open);
  const setOpen = useCommandPaletteStore((state) => state.setOpen);
  const toggle = useCommandPaletteStore((state) => state.toggle);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const entries = useMemo(
    () => flattenNavForPalette(filterSidebarGroupsForUser(user, permissions.isSuccess ? permissions.permissionMap : undefined)),
    [permissions.isSuccess, permissions.permissionMap, user],
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (needle ? entries.filter((entry) => `${entry.label} ${entry.group} ${entry.href}`.toLowerCase().includes(needle)) : entries).slice(0, 40);
  }, [entries, query]);

  const close = useCallback(() => setOpen(false), [setOpen]);
  const navigate = useCallback((href: string) => {
    close();
    setQuery("");
    routerNavigate(href);
  }, [close, routerNavigate]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggle();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [toggle]);
  useEffect(() => {
    setActiveIndex(0);
    if (!open) setQuery("");
  }, [open, query]);

  return (
    <Modal open={open} onClose={close} title="Search workspace" description="Jump to any section available for your role." size="lg" className="sm:max-w-xl">
      <SearchInput
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search pages and tools"
        aria-label="Search pages and tools"
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((index) => Math.min(index + 1, Math.max(0, filtered.length - 1)));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((index) => Math.max(0, index - 1));
          } else if (event.key === "Enter" && filtered[activeIndex]) {
            event.preventDefault();
            navigate(filtered[activeIndex].href);
          }
        }}
      />
      <p className="mt-ds-2 text-micro uppercase tracking-[0.08em] text-muted">Ctrl/⌘ K to toggle · ↑↓ to navigate · Enter to open</p>
      <div className="mt-ds-3 max-h-[min(52vh,26rem)] overflow-y-auto" role="listbox" aria-label="Available destinations">
        {filtered.length === 0 ? <p className="rounded-control bg-surface-inset px-ds-3 py-ds-5 text-center text-caption text-muted">No matching destinations</p> : filtered.map((entry, index) => (
          <button
            key={entry.id}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            className={cn("flex min-h-11 w-full items-center gap-ds-3 rounded-control px-ds-3 py-ds-2 text-left transition-colors duration-fast", index === activeIndex ? "bg-accent-muted text-foreground" : "text-foreground hover:bg-surface-hover")}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => navigate(entry.href)}
          >
            <span className="min-w-0 flex-1"><span className="block truncate text-caption font-semibold">{entry.label}</span><span className="block truncate text-micro text-muted">{entry.group}</span></span>
            <ArrowRight className={cn("h-4 w-4 shrink-0", index === activeIndex ? "text-accent" : "text-muted")} aria-hidden />
          </button>
        ))}
      </div>
    </Modal>
  );
}
